<template>
  <v-dialog :value="value" max-width="820px" persistent @input="$emit('input', $event)">
    <v-card :loading="loading" :disabled="loading">
      <v-card-title class="justify-center">
        <span class="text-h5">{{ $t('demos.create') }}</span>
      </v-card-title>

      <v-card-text v-if="!createdLink">
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-row>
            <v-col cols="12" class="py-1">
              <div class="text-caption grey--text text--darken-2 mb-1">{{ $t('demos.account_type') }}</div>
              <v-radio-group v-model="form.accountType" row hide-details class="mt-0 pt-0">
                <v-radio :label="$t('demos.type_student')" :value="0" />
                <v-radio :label="$t('demos.type_teacher')" :value="2" />
              </v-radio-group>
            </v-col>
            <v-col cols="12" class="py-1">
              <div class="text-caption grey--text text--darken-2 mb-1">{{ $t('demos.mode') }}</div>
              <v-radio-group v-model="form.mode" row hide-details class="mt-0 pt-0">
                <v-radio :label="$t('demos.mode_reuse')" value="reuse" />
                <v-radio :label="$t('demos.mode_new')" value="new" />
              </v-radio-group>
            </v-col>
          </v-row>

          <v-row v-if="form.mode === 'reuse'">
            <v-col cols="12">
              <v-autocomplete
                v-model="form.bookUserId"
                :items="candidates"
                :loading="loadingCandidates"
                item-text="name"
                item-value="id"
                :label="$t('demos.account')"
                :rules="[rules.required]"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle v-if="candidateMeta(item)">{{ candidateMeta(item) }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
                <template v-slot:selection="{ item }">
                  {{ item.name }}<span v-if="candidateMeta(item)" class="grey--text text--darken-1 ml-1">· {{ candidateMeta(item) }}</span>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <v-row v-else>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.newUser.firstname" :label="$t('demos.firstname')" :rules="[rules.required]" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.newUser.lastname" :label="$t('demos.lastname')" :rules="[rules.required]" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.newUser.identifier" :label="$t('demos.identifier')" :rules="[rules.required]" maxlength="100" />
            </v-col>
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="form.newUser.baseCountryId"
                :items="countries"
                :loading="loadingCountries"
                item-text="name"
                item-value="id"
                :label="$t('demos.country')"
                :rules="[rules.positive]"
                @change="onCountryChange"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="form.newUser.schoolId"
                :items="schools"
                :loading="loadingSchools"
                :disabled="!form.newUser.baseCountryId"
                item-text="name"
                item-value="id"
                :label="$t('demos.school')"
                :rules="[rules.positive]"
                @change="onSchoolChange"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="form.newUser.courseId"
                :items="courses"
                :loading="loadingCourses"
                :disabled="!form.newUser.schoolId"
                item-text="grade"
                item-value="id"
                :label="$t('demos.grade')"
                clearable
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.client" :label="$t('demos.client')" :rules="[rules.required]" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.reason" :label="$t('demos.reason')" />
            </v-col>
            <v-col cols="12" md="6">
              <v-menu v-model="startMenu" :close-on-content-click="false" min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.startingDate"
                    :label="$t('demos.starting_date')"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    :rules="[rules.required]"
                  />
                </template>
                <v-date-picker v-model="form.startingDate" @input="startMenu = false" />
              </v-menu>
            </v-col>
            <v-col cols="12" md="6">
              <v-menu v-model="finishMenu" :close-on-content-click="false" min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.finishDate"
                    :label="$t('demos.finish_date')"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    :rules="[rules.required, rules.afterStart]"
                  />
                </template>
                <v-date-picker v-model="form.finishDate" :min="form.startingDate" @input="finishMenu = false" />
              </v-menu>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-text v-else class="text-center">
        <v-icon size="48" color="green">mdi-check-circle</v-icon>
        <h3 class="my-2">{{ $t('demos.created_title') }}</h3>
        <v-text-field :value="createdLink" :label="$t('demos.link')" readonly append-icon="mdi-content-copy" @click:append="copyLink" />
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn color="grey" text @click="close">{{ createdLink ? $t('demos.all_demos') : $t('common.cancel') }}</v-btn>
        <v-btn v-if="!createdLink" color="deep-purple" text :disabled="!valid || loading" @click="save">
          {{ $t('common.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import demosService from '@/services/demos.service';
import snackbarMixin from '../../../mixins/snackbar';

const emptyForm = () => ({
  mode: 'reuse',
  accountType: 0,
  startingDate: '',
  finishDate: '',
  client: '',
  reason: '',
  bookUserId: undefined,
  newUser: {
    firstname: '', lastname: '', identifier: '', baseCountryId: null, schoolId: null, courseId: undefined,
  },
});

export default {
  name: 'CreateDemoModal',
  mixins: [snackbarMixin],
  props: { value: { type: Boolean, default: false } },
  data() {
    return {
      valid: false,
      loading: false,
      loadingCandidates: false,
      loadingCountries: false,
      loadingSchools: false,
      loadingCourses: false,
      startMenu: false,
      finishMenu: false,
      candidates: [],
      countries: [],
      schools: [],
      courses: [],
      createdLink: '',
      form: emptyForm(),
      rules: {
        required: (v) => (v !== null && v !== undefined && v !== '') || this.$t('common.field_required'),
        positive: (v) => (v !== null && v !== undefined && v !== '' && Number(v) > 0) || this.$t('common.field_required'),
        afterStart: (v) => (!this.form.startingDate || v > this.form.startingDate) || this.$t('demos.finish_date'),
      },
    };
  },
  computed: {
    linkBase() {
      return process.env.VUE_APP_DEMO_LINK_BASE_URL || 'https://auth.beeverso.org/demo';
    },
  },
  watch: {
    value(open) {
      if (open) this.loadCandidates();
    },
    'form.accountType': function reloadOnType() {
      this.form.bookUserId = undefined;
      if (this.value && this.form.mode === 'reuse') this.loadCandidates();
    },
    'form.mode': function loadOnMode(mode) {
      if (mode === 'new' && this.countries.length === 0) this.loadCountries();
    },
  },
  methods: {
    candidateMeta(item) {
      const parts = [];
      if (item.grade) parts.push(item.grade);
      if (item.country) parts.push(item.country);
      if (item.school) parts.push(item.school);
      return parts.join(' · ');
    },
    async loadCandidates() {
      this.loadingCandidates = true;
      try {
        this.candidates = await demosService.candidates({ userType: this.form.accountType });
      } finally {
        this.loadingCandidates = false;
      }
    },
    async loadCountries() {
      this.loadingCountries = true;
      try {
        this.countries = await demosService.countries();
      } finally {
        this.loadingCountries = false;
      }
    },
    async onCountryChange() {
      this.form.newUser.schoolId = null;
      this.form.newUser.courseId = undefined;
      this.schools = [];
      this.courses = [];
      if (!this.form.newUser.baseCountryId) return;
      this.loadingSchools = true;
      try {
        this.schools = await demosService.schools(this.form.newUser.baseCountryId);
      } finally {
        this.loadingSchools = false;
      }
    },
    async onSchoolChange() {
      this.form.newUser.courseId = undefined;
      this.courses = [];
      if (!this.form.newUser.schoolId) return;
      this.loadingCourses = true;
      try {
        this.courses = await demosService.courses(this.form.newUser.schoolId);
      } finally {
        this.loadingCourses = false;
      }
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      this.loading = true;
      try {
        const payload = this.form.mode === 'reuse'
          ? { ...this.form, newUser: undefined }
          : { ...this.form, bookUserId: undefined };
        const result = await demosService.create(payload);
        this.createdLink = `${this.linkBase}?token=${result.content.activationCode}`;
        this.$emit('onSave');
      } catch (error) {
        const msg = (error && error.response && error.response.data && error.response.data.message)
          || this.$t('demos.create_error');
        this.snackbarError(msg);
      } finally {
        this.loading = false;
      }
    },
    copyLink() {
      navigator.clipboard.writeText(this.createdLink);
      this.snackbarSuccess(this.$t('demos.copied'));
    },
    close() {
      this.createdLink = '';
      this.form = emptyForm();
      this.schools = [];
      this.courses = [];
      const { form } = this.$refs;
      if (form && form.resetValidation) form.resetValidation();
      this.$emit('input', false);
    },
  },
};
</script>
